import { createRoutine } from 'redux-saga-routines'
import { eventChannel } from 'redux-saga'
import {
  put,
  call,
  takeLatest,
  fork,
  delay,
  select,
  race,
  take
} from '@redux-saga/core/effects'
import { logoutUserRoutine } from 'features/auth/ducks/actions'
import { isUserLoggedIn, getCurrentUser } from 'features/auth/ducks/selectors'
import { toast } from 'react-toastify'
import { getSessionRemainingSeconds } from './selectors'
import * as sessionService from 'services/sessionService'
import { isOnline as checkIsOnline } from 'ducks/offline/actions'
import { KEYS } from 'utils/localStorage'
import LocalStorageService from 'services/LocalStorageService'

const SESSION_CHECK_INTERVAL = 60000 // Check every minute
const ACTIVITY_EXTEND_INTERVAL = 5 * 60 * 1000
const ACTIVITY_EVENT_THROTTLE = 30 * 1000
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'input', 'wheel', 'touchstart']

let lastExtendedAt = Date.now()

const parseSessionPayload = (data) => {
  const expiresAt = new Date(data.expiresAt)
  return {
    expiresAt: isNaN(expiresAt.getTime()) ? new Date(Date.now() + 1800000) : expiresAt,
    remainingSeconds: Math.max(0, parseInt(data.remainingSeconds) || 0),
    warningThresholdSeconds: parseInt(data.warningMinutes) * 60 || 300
  }
}

export const checkSessionStatusRoutine = createRoutine('CHECK_SESSION_STATUS')
export const refreshSessionRoutine = createRoutine('REFRESH_SESSION')
export const sessionExpiringRoutine = createRoutine('SESSION_EXPIRING')
export const sessionExpiredRoutine = createRoutine('SESSION_EXPIRED')
export const dismissSessionWarningRoutine = createRoutine(
  'DISMISS_SESSION_WARNING'
)
export const initSessionListenerRoutine = createRoutine('INIT_SESSION_LISTENER')

function* initSessionListener() {
  yield put(initSessionListenerRoutine.request())
  try {
    if (!checkIsOnline()) {
      yield put(initSessionListenerRoutine.success({ expiresAt: new Date(Date.now() + 3600000), remainingSeconds: 3600, warningThresholdSeconds: 300 }))
      return
    }
    const isLoggedIn = yield select(isUserLoggedIn)
    if (!isLoggedIn) return

    const response = yield call(sessionService.checkSession)
    const data = response.data.data || response.data // Handle both formats

    // Ensure we have a valid date
    let expiresAt = null
    try {
      expiresAt = new Date(data.expiresAt)
      // Check if date is valid
      if (isNaN(expiresAt.getTime())) {
        console.error('Invalid expires_at date:', data.expiresAt)
        expiresAt = new Date(Date.now() + 1800000) // Default 30 mins
      }
    } catch (e) {
      console.error('Error parsing expires_at:', e)
      expiresAt = new Date(Date.now() + 1800000) // Default 30 mins
    }

    // Ensure remaining seconds is valid
    const remainingSeconds = Math.max(0, parseInt(data.remainingSeconds) || 0)

    // Convert warning minutes to seconds
    const warningThresholdSeconds = parseInt(data.warningMinutes) * 60 || 300

    yield put(
      initSessionListenerRoutine.success({
        expiresAt,
        remainingSeconds,
        warningThresholdSeconds
      })
    )

    lastExtendedAt = Date.now()

    // Start periodic session checking
    yield fork(sessionCheckWatcher)
  } catch (error) {
    console.error('Failed to initialize session listener:', error)
    yield put(initSessionListenerRoutine.failure(error))
  }
}

function* checkSessionStatus() {
  yield put(checkSessionStatusRoutine.request())
  try {
    if (!checkIsOnline()) return
    const isLoggedIn = yield select(isUserLoggedIn)
    if (!isLoggedIn) return

    const response = yield call(sessionService.checkSession)
    const data = response.data.data || response.data // Handle both formats

    // Ensure we have a valid date
    let expiresAt = null
    try {
      expiresAt = new Date(data.expiresAt)
      // Check if date is valid
      if (isNaN(expiresAt.getTime())) {
        console.error('Invalid expires_at date:', data.expiresAt)
        expiresAt = new Date(Date.now() + 1800000) // Default 30 mins
      }
    } catch (e) {
      console.error('Error parsing expires_at:', e)
      expiresAt = new Date(Date.now() + 1800000) // Default 30 mins
    }
    // Ensure remaining seconds is valid
    const remainingSeconds = Math.max(0, parseInt(data.remainingSeconds) || 0)

    // Convert warning minutes to seconds
    const warningThresholdSeconds = parseInt(data.warningMinutes) * 60 || 300

    yield put(
      checkSessionStatusRoutine.success({
        expiresAt,
        remainingSeconds,
        warningThresholdSeconds
      })
    )

    // Check if session is about to expire
    if (remainingSeconds <= warningThresholdSeconds && remainingSeconds > 0) {
      yield put(sessionExpiringRoutine({ remainingSeconds }))
    } else if (remainingSeconds <= 0) {
      yield put(sessionExpiredRoutine())
      yield put(logoutUserRoutine())
    }
  } catch (error) {
    console.error('Failed to check session status:', error)
    yield put(checkSessionStatusRoutine.failure(error))
  }
}

function* refreshSession() {
  yield put(refreshSessionRoutine.request())
  try {
    const isLoggedIn = yield select(isUserLoggedIn)
    if (!isLoggedIn) return

    const response = yield call(sessionService.extendSession)
    const data = response.data.data || response.data // Handle both formats

    // Ensure we have a valid date
    let expiresAt = null
    try {
      expiresAt = new Date(data.expiresAt)
      // Check if date is valid
      if (isNaN(expiresAt.getTime())) {
        console.error('Invalid expires_at date:', data.expiresAt)
        expiresAt = new Date(Date.now() + 1800000) // Default 30 mins
      }
    } catch (e) {
      console.error('Error parsing expires_at:', e)
      expiresAt = new Date(Date.now() + 1800000) // Default 30 mins
    }

    // Ensure remaining seconds is valid
    const remainingSeconds = Math.max(0, parseInt(data.remainingSeconds) || 0)

    // Convert warning minutes to seconds
    const warningThresholdSeconds = parseInt(data.warningMinutes) * 60 || 300

    yield put(
      refreshSessionRoutine.success({
        expiresAt,
        remainingSeconds,
        warningThresholdSeconds
      })
    )

    lastExtendedAt = Date.now()
    toast.success('Your session has been extended')
  } catch (error) {
    console.error('Failed to refresh session:', error)
    yield put(refreshSessionRoutine.failure(error))
    toast.error('Failed to extend your session. Please try again.')
  }
}

function* handleSessionExpiring({ payload }) {
  const { remainingSeconds } = payload

  // Wait for user action or session expiry
  const { expired } = yield race({
    expired: delay(remainingSeconds * 1000),
    dismissed: take(dismissSessionWarningRoutine.TRIGGER),
    refreshed: take([refreshSessionRoutine.TRIGGER, refreshSessionRoutine.SUCCESS])
  })

  if (expired) {
    yield call(checkSessionStatus)
  }
}

function createActivityChannel() {
  return eventChannel((emit) => {
    let lastEmit = 0
    const handler = () => {
      const now = Date.now()
      if (now - lastEmit < ACTIVITY_EVENT_THROTTLE) return
      lastEmit = now
      emit(now)
    }
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, handler, { capture: true, passive: true })
    )
    return () => {
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, handler, { capture: true })
      )
    }
  })
}

export function* extendOnActivity() {
  if (Date.now() - lastExtendedAt < ACTIVITY_EXTEND_INTERVAL) return
  if (!checkIsOnline()) return
  const isLoggedIn = yield select(isUserLoggedIn)
  if (!isLoggedIn) return

  lastExtendedAt = Date.now()
  try {
    const response = yield call(sessionService.extendSession)
    const data = response.data.data || response.data
    yield put(refreshSessionRoutine.success(parseSessionPayload(data)))
  } catch (error) {
    lastExtendedAt = 0
  }
}

function* userActivityWatcher() {
  const chan = yield call(createActivityChannel)
  try {
    while (true) {
      yield take(chan)
      yield call(extendOnActivity)
    }
  } finally {
    if (chan && chan.close) chan.close()
  }
}

function* sessionCheckLoop() {
  while (true) {
    yield call(checkSessionStatus)
    yield delay(SESSION_CHECK_INTERVAL)
  }
}

// Background tabs throttle setTimeout/setInterval (and saga `delay`) to ~1/min
// or pause them entirely. The polling loop alone is unreliable for catching
// session expiry while the tab is hidden. These channels react to the user
// coming back AND to the API returning 401/419, so expiry is detected at the
// earliest possible moment.

function createVisibilityChannel() {
  return eventChannel((emit) => {
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') emit('visible')
    }
    const focusHandler = () => emit('focus')
    const onlineHandler = () => emit('online')
    document.addEventListener('visibilitychange', visibilityHandler)
    window.addEventListener('focus', focusHandler)
    window.addEventListener('online', onlineHandler)
    return () => {
      document.removeEventListener('visibilitychange', visibilityHandler)
      window.removeEventListener('focus', focusHandler)
      window.removeEventListener('online', onlineHandler)
    }
  })
}

function* tabVisibilityWatcher() {
  const chan = yield call(createVisibilityChannel)
  try {
    while (true) {
      yield take(chan)
      const isLoggedIn = yield select(isUserLoggedIn)
      if (isLoggedIn) yield put(checkSessionStatusRoutine())
    }
  } finally {
    if (chan && chan.close) chan.close()
  }
}

function createApiUnauthorizedChannel() {
  return eventChannel((emit) => {
    const handler = (e) => emit(e?.detail?.status || 401)
    window.addEventListener('app:session-expired', handler)
    return () => window.removeEventListener('app:session-expired', handler)
  })
}

function* apiUnauthorizedWatcher() {
  const chan = yield call(createApiUnauthorizedChannel)
  try {
    while (true) {
      yield take(chan)
      const isLoggedIn = yield select(isUserLoggedIn)
      if (!isLoggedIn) continue
      toast.error('Your session has expired. Please log in again.', {
        toastId: 'session-expired',
        autoClose: 6000
      })
      yield put(sessionExpiredRoutine())
      yield put(logoutUserRoutine())
    }
  } finally {
    if (chan && chan.close) chan.close()
  }
}

// Cross-tab logout sync. The `storage` event fires in OTHER tabs when the
// token key is changed/removed — so if you log out in one tab, every other
// tab notices within milliseconds rather than on next API call. A token that
// is merely reissued for the SAME person is not a sign-out: requests read the
// token from storage on every call, so this tab just picks the new one up.
function createTokenStorageChannel() {
  return eventChannel((emit) => {
    const handler = (e) => {
      if (e.key !== KEYS.token) return
      emit({ removed: e.newValue == null })
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  })
}

function* tokenStorageWatcher() {
  const chan = yield call(createTokenStorageChannel)
  try {
    while (true) {
      const { removed } = yield take(chan)
      const isLoggedIn = yield select(isUserLoggedIn)
      if (!isLoggedIn) continue

      if (!removed) {
        const currentUser = yield select(getCurrentUser)
        const storedUserId = LocalStorageService.get(KEYS.userId)
        const sameUser =
          !currentUser?.id ||
          !storedUserId ||
          String(storedUserId) === String(currentUser.id)
        if (sameUser) continue

        toast.info('A different account signed in on another tab.', {
          toastId: 'cross-tab-identity',
          autoClose: 6000
        })
      } else {
        toast.info('Your session ended in another tab. Please sign in again.', {
          toastId: 'cross-tab-logout',
          autoClose: 5000
        })
      }

      yield put(sessionExpiredRoutine())
      yield put(logoutUserRoutine())
    }
  } finally {
    if (chan && chan.close) chan.close()
  }
}

// WATCHERS
function* sessionCheckWatcher() {
  yield fork(sessionCheckLoop)
}

function* initSessionListenerWatcher() {
  yield takeLatest(initSessionListenerRoutine.TRIGGER, initSessionListener)
}

function* checkSessionStatusWatcher() {
  yield takeLatest(checkSessionStatusRoutine.TRIGGER, checkSessionStatus)
}

function* refreshSessionWatcher() {
  yield takeLatest(refreshSessionRoutine.TRIGGER, refreshSession)
}

function* sessionExpiringWatcher() {
  yield takeLatest(sessionExpiringRoutine.TRIGGER, handleSessionExpiring)
}

// SAGAS
export const sessionSagas = [
  fork(initSessionListenerWatcher),
  fork(checkSessionStatusWatcher),
  fork(refreshSessionWatcher),
  fork(sessionExpiringWatcher),
  fork(tabVisibilityWatcher),
  fork(apiUnauthorizedWatcher),
  fork(tokenStorageWatcher),
  fork(userActivityWatcher)
]
